import { Card, CardContent } from "@/components/ui/card";
import { TodayData, DashboardFilters } from "@/types/dashboard";
import { Mask } from "@/utils/mask";
import { Zap, TrendingUp, Calendar, Target } from "lucide-react";

interface TodayCardLargeProps {
  data: TodayData;
  period?: DashboardFilters["period"];
  goalType?: "meta" | "super_meta";
  requiredDailyAverage?: number;
}

const periodLabels: Record<string, string> = {
  today: "Hoje",
  yesterday: "Ontem",
  week: "Semana",
  month: "Mês",
  custom: "Período",
};

export function TodayCardLarge({ data, period = "today", goalType = "meta", requiredDailyAverage = 0 }: TodayCardLargeProps) {
  const periodLabel = periodLabels[period || "today"] || "Hoje";
  const dailyGoal = requiredDailyAverage > 0 ? requiredDailyAverage : data.daily_goal;
  const progress = dailyGoal > 0 ? (data.sales_total / dailyGoal) * 100 : 0;
  const goalLabel = goalType === "super_meta" ? "Super Meta" : "Meta";
  const remaining = Math.max(0, dailyGoal - data.sales_total);
  const isGoalReached = progress >= 100;

  return (
    <Card className="bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 border-0 text-white overflow-hidden">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Zap className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Vendas - {periodLabel}</h3>
              <p className="text-sm text-blue-100">Total vendido no período</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm">
            <Calendar className="w-4 h-4" />
            {periodLabel}
          </div>
        </div>

        {/* Valor Principal */}
        <div className="mb-4">
          <p className="text-4xl font-bold mb-2">
            {Mask.formatCurrency(data.sales_total)}
          </p>
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-blue-400/30">
            <TrendingUp className="w-4 h-4" />
            {data.order_count} pedidos
          </div>
        </div>

        {/* Métricas */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/20">
          <div>
            <p className="text-xs text-blue-100 mb-1">{goalLabel} diária</p>
            <p className="text-lg font-semibold">{Mask.formatCurrency(dailyGoal)}</p>
            <p className="text-xs text-blue-200 flex items-center gap-1">
              <Target className="w-3 h-3" />
              {progress.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-xs text-blue-100 mb-1">Progresso</p>
            <p className="text-lg font-semibold">{progress.toFixed(1)}%</p>
            <div className="mt-1">
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <p className="text-xs text-blue-200 mt-0.5">
                {isGoalReached ? "Meta atingida!" : `Faltam ${Mask.formatCurrency(remaining)}`}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs text-blue-100 mb-1">Pedidos</p>
            <p className="text-lg font-semibold">{data.order_count}</p>
            <p className="text-xs text-blue-200 flex items-center gap-1">
              no período
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
